import { api } from './api';
import type {
  ProposedArticle,
  ProposedArticleStatus,
  NewsKeywordSuggestion,
  GeneratedArticle,
  TargetEvaluation,
  ContentTopic,
  CreateContentTopicInput,
} from '@traaker/shared';

export const proposedArticlesApi = {
  list: (brandId: string, status?: ProposedArticleStatus) => {
    const params = new URLSearchParams({ brandId });
    if (status) params.set('status', status);
    return api.get<ProposedArticle[]>(`/proposed-articles?${params.toString()}`);
  },

  // Cursor-paginated hub list (10/page). Cheap DDB read (native Query + Limit).
  page: (
    brandId: string,
    opts: { limit?: number; cursor?: string; includeArchived?: boolean } = {},
  ) => {
    const params = new URLSearchParams({ brandId });
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    if (opts.includeArchived) params.set('includeArchived', 'true');
    return api.get<{ items: ProposedArticle[]; nextCursor: string | null }>(
      `/proposed-articles/page?${params.toString()}`,
    );
  },

  getOne: (articleId: string, brandId: string) =>
    api.get<ProposedArticle>(`/proposed-articles/${articleId}?brandId=${brandId}`),

  updateStatus: (
    articleId: string,
    body: { brandId: string; status: ProposedArticleStatus; publishedUrl?: string; createdAt?: string },
  ) => api.patch<ProposedArticle>(`/proposed-articles/${articleId}`, body),

  updateArticle: (
    articleId: string,
    body: { brandId: string; article: Partial<GeneratedArticle> },
  ) => api.patch<ProposedArticle>(`/proposed-articles/${articleId}/article`, body),

  getSuggestedKeywords: (brandId: string) =>
    api.get<NewsKeywordSuggestion[]>(`/proposed-articles/suggested-keywords?brandId=${brandId}`),

  evaluate: (articleId: string, body: { brandId: string; target: string; manual?: boolean }) =>
    api.post<TargetEvaluation>(`/proposed-articles/${articleId}/evaluate`, body),

  // ── Content topics (saved content recommendations, pickable in the Studio) ──
  listContentTopics: (brandId: string) =>
    api.get<ContentTopic[]>(`/proposed-articles/content-topics?brandId=${brandId}`),

  createContentTopic: (body: CreateContentTopicInput & { brandId: string }) =>
    api.post<ContentTopic>('/proposed-articles/content-topics', body),

  deleteContentTopic: (topicId: string, brandId: string) =>
    api.delete<{ success: boolean }>(`/proposed-articles/content-topics/${topicId}?brandId=${brandId}`),
};
